import dayjs from 'dayjs';
import ThumbsUpIcon from 'ocus-styleguide/src/assets/svg/thumbs-up.svg';
import { Notifier } from '@we-are-ocus/ocus-components';

import MissionCollection from 'ocus-shared-resources/src/Domains/Mission/MissionCollection';
import {
  assignMission,
  event as sendMissionEvent,
} from '~/domains/Mission/api';
import { getErrorMessage } from '~/helpers/errorProcessor';
import MissionsAPI from '~/services/api/MissionsAPI';
import { MergedQueryParameters } from '~/services/api/api-utils';

export const state = () => ({
  lastPath: '',
  isCartOpen: false,
  inCartMissions: [],
  isCartValidationOperate: true,
  isInCartMissionsFetching: false,
  isRemoveMissionInCartOperate: true,
  middlewareCartHaveFetchOnce: true,
});

export const mutations = {
  setLastPath(state, newVal) {
    state.lastPath = newVal;
  },
  setIsCartOpen(state, newVal) {
    state.isCartOpen = newVal;
  },
  setInCartMissions(state, newVal) {
    state.inCartMissions = newVal || [];
  },
  setIsInCartMissionsFetching(state, newVal) {
    state.isInCartMissionsFetching = newVal;
  },
  setIsCartValidationOperate(state, newVal) {
    state.isCartValidationOperate = newVal;
  },
  setIsRemoveMissionInCartOperate(state, newVal) {
    state.isRemoveMissionInCartOperate = newVal;
  },
  setMiddlewareCartHaveFetchOnce(state, newVal) {
    state.middlewareCartHaveFetchOnce = newVal;
  },
};

export const actions = {
  async missionIsRemovedFromCartNotification({ dispatch }, removedMission, alert) {
    const title = removedMission.name;
    if (alert) {
      dispatch('ui/alertInfo', {
        title: title + this.$vuei18next.t(
          'store-cart.remove-from-cart-notif',
          ' has been removed from your cart',
        ),
      }, { root: true });
    }
  },
  async missionsInCartIsValidatedWithSuccessNotification({ dispatch }) {
    dispatch('ui/alert', {
      icon: ThumbsUpIcon,
      title: this.$vuei18next.t(
        'store-cart.assignation-success-title',
        'The missions have been assigned to you',
      ),
      description: this.$vuei18next.t(
        'store-cart.assignation-success-description',
        'Find them in your missions section and follow the instructions to achieve it'
        + ' successfully',
      ),
    }, { root: true });
  },
  async missionSuccessfullyAddedToTheCart({ dispatch }, addedMission) {
    const missionName = addedMission.name;
    dispatch('ui/alert', {
      icon: ThumbsUpIcon,
      title:
        `${this.$vuei18next.t('store-cart.mission', 'Mission: ')
        + missionName
        } ${
          this.$vuei18next.t(
            'store-cart.mission-in-cart-added-success-title',
            'added to the cart',
          )}`,
    }, { root: true });
  },
  async fetchUserCart({ commit, state, dispatch }, { noRemoveNotification } = { noRemoveNotification: false }) {
    const queryParams = new MergedQueryParameters({
      per_page: 'all',
    });
    queryParams.addFilter({ status: 'WAITING_ATTRIBUTION' });
    commit('setIsInCartMissionsFetching', true);
    await MissionsAPI.fetchMissionList(queryParams.params, null).then(async (missions) => {
      const collection = new MissionCollection(missions?.data);
      const tmp = state.inCartMissions;
      const missionCollection = collection.sort((a, b) => (a.selected_at?.isAfter(b.selected_at) ? 1 : -1));

      let missionList = [];
      if (tmp.length > missionCollection.length) {
        missionList = tmp;
      } else {
        missionCollection.getItems().forEach((mission) => {
          if (!tmp.includes(({ id }) => mission.id === id)) {
            missionList.push(mission);
          }
        });
      }
      const promises = [];
      missionList.forEach(async (mission) => {
        promises.push(MissionsAPI.fetchMissionCart(mission.id));
      });
      const listMission = [];
      try {
        await Promise.all(promises).then((missionsPromise) => {
          missionsPromise.forEach((mission) => listMission.push(mission.data));
        });
      } catch (error) {
        console.log(error);
      }
      const missionsListCollection = new MissionCollection({ data: listMission });
      commit('setInCartMissions', missionsListCollection.getItems());

      if (state.middlewareCartHaveFetchOnce) {
        commit('setMiddlewareCartHaveFetchOnce', false);
      }
      commit('setIsInCartMissionsFetching', false);
      if (noRemoveNotification) return;
      const missionIds = missionCollection?.map(({ id }) => id);
      tmp.forEach(
        (mission) => missionIds.includes(mission.id)
          || dispatch('missionIsRemovedFromCartNotification', mission, true),
      );
    });
  },

  async addMissionInCart({
    commit, dispatch, state, rootGetters,
  }, { toAdd, addSuccess = () => {}, addFail = () => {} }) {
    if (state.inCartMissions.length > 9) {
      Notifier.error({
        message: this.$vuei18next.t(
          'store-cart.fail-to-much-mission',
          'Too much missions in your cart. Please valid your selection or remove a mission',
        ),
      });
    } else {
      try {
        if (toAdd) {
          await sendMissionEvent({ id: toAdd.id, attributes: { code: 'WA:SELECTION' } });
          toAdd.selected_at = dayjs();
          toAdd.operator = await rootGetters['members/me'];
          commit('setInCartMissions', [...state.inCartMissions, toAdd]);
          dispatch('missionSuccessfullyAddedToTheCart', toAdd);
          if (this.$device.isDesktop) dispatch('openCart');
          addSuccess();
        } else {
          Notifier.error({
            message: this.$vuei18next.t(
              'store-cart.fail-cart-add',
              'Fail to add mission in cart',
            ),
          });
          addFail();
        }
      } catch (error) {
        getErrorMessage(error).forEach((message) => {
          Notifier.error({
            message,
          });
        });
        addFail();
      }
    }
  },
  async removeMissionById({ commit, state }, id) {
    commit('setInCartMissions', state.inCartMissions.filter((el) => id !== el.id));
  },
  async removeMissionInCart(
    { commit, dispatch, state },
    { toRemoveId: id, removeSuccess = () => {}, removeFail = () => {} },
  ) {
    commit('setIsRemoveMissionInCartOperate', true);
    const tmp = state.inCartMissions.find((el) => id === el.id);
    try {
      await sendMissionEvent({ id, attributes: { code: 'WA:DESELECTION' } });
      commit('setInCartMissions', state.inCartMissions.filter((el) => id !== el.id));
      dispatch('missionIsRemovedFromCartNotification', tmp, true);
      removeSuccess();
    } catch (error) {
      getErrorMessage(error).forEach((message) => {
        Notifier.error({
          message,
        });
      });
      removeFail();
    }
    commit('setIsRemoveMissionInCartOperate', false);
  },
  async cartValidation(
    {
     dispatch, state, commit,
    },
    { validationSuccess = () => {}, validationFail = () => {} },
  ) {
    commit('setIsCartValidationOperate', true);
    window.$nuxt.$loading.start();
    try {
      const promises = [];
      state.inCartMissions.forEach(({ id }) => {
        promises.push(new Promise((resolve) => {
          assignMission(id);
          setTimeout(resolve, 300);
        }));
      });
      await Promise.all(promises);
      await dispatch('fetchUserCart', { noRemoveNotification: true });
      await dispatch('missionsInCartIsValidatedWithSuccessNotification');
      dispatch('closeCart');
      commit('setInCartMissions', []);
      validationSuccess();
    } catch (error) {
      getErrorMessage(error).forEach((message) => {
        Notifier.error({
          message,
        });
      });
      validationFail();
    } finally {
      window.$nuxt.$loading.finish();
    }
    commit('setIsCartValidationOperate', false);
  },
  removeFromCart(
    { commit, dispatch, state },
    { toRemoveId: id, removeFail = () => {} },
  ) {
    commit('setIsRemoveMissionInCartOperate', true);
    const tmp = state.inCartMissions.find((el) => id === el.id);
    try {
      commit('setInCartMissions', state.inCartMissions.filter((el) => id !== el.id));
      dispatch('missionIsRemovedFromCartNotification', tmp, false);
    } catch (error) {
      getErrorMessage(error).forEach((message) => {
        Notifier.error({
          message,
        });
      });
      removeFail();
    }
    commit('setIsRemoveMissionInCartOperate', false);
  },
  async openCart({ commit }) {
    commit('setIsCartOpen', true);
  },
  async closeCart({ commit }) {
    commit('setIsCartOpen', false);
  },
  async switchIsCartOpen({ commit, state }) {
    commit('setIsCartOpen', !state.isCartOpen);
  },
};
